import { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "../admin/Modal";
import { IconButton } from "./IconButton";
import { CheckIcon, CopyIcon, ExternalIcon } from "./ToolIcons";
import { ApiStyles, AvailabilityUrl, CompatibilityUrl, KeyVariable, Languages, apiExample, regionOf, type ApiStyle, type Language } from "./bedrockApi";
import styles from "./BedrockApiGuide.module.css";

/**
 * The popup behind the Endpoint section: worked examples of calling Bedrock with the person's own key and models, in the two
 * request styles most people need, and links to AWS's tables of which model takes which API at which endpoint. The examples
 * never contain the key itself, only the variable it lives in. A copy is confirmed on the button itself: a toast would sit
 * underneath the modal, which the browser draws above everything else.
 */
export function BedrockApiGuide({
  endpoint,
  models,
  onClose,
}: {
  endpoint: string;
  models: string[];
  onClose: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const modelSelectId = useId();
  const [language, setLanguage] = useState<Language>("curl");
  const [model, setModel] = useState(models[0] ?? "<model id>");
  const region = regionOf(endpoint);
  const [copied, setCopied] = useState<{ style: ApiStyle; ok: boolean } | null>(null);

  useEffect(() => {
    if (copied === null) {
      return undefined;
    }
    const timer = window.setTimeout(() => setCopied(null), 2000);
    return () => window.clearTimeout(timer);
  }, [copied]);

  const copy = async (style: ApiStyle, code: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied({ style, ok: true });
    } catch {
      setCopied({ style, ok: false });
    }
  };

  return (
    <Modal title={t("providerAccess.guide.title")} onClose={onClose} wide>
      <div className={styles.guide}>
        <p className={styles.intro}>{t("providerAccess.guide.intro", { variable: KeyVariable })}</p>

        <div className={styles.controls}>
          {models.length > 1 && (
            <label className={styles.modelPicker} htmlFor={modelSelectId}>
              <span>{t("providerAccess.guide.model")}</span>
              <select id={modelSelectId} value={model} onChange={(event) => setModel(event.target.value)}>
                {models.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>
          )}
          <div className={styles.segmented} role="group" aria-label={t("providerAccess.guide.language")}>
            {Languages.map((option) => (
              <button key={option} type="button" aria-pressed={language === option} onClick={() => setLanguage(option)}>
                {t(`providerAccess.guide.languages.${option}`)}
              </button>
            ))}
          </div>
        </div>

        {ApiStyles.map((style) => {
          const code = apiExample(style, language, endpoint, model, region ?? "<region>");
          return (
            <section key={style} className={styles.example} aria-labelledby={`guide-${style}`}>
              <div className={styles.exampleHeader}>
                <div>
                  <h3 id={`guide-${style}`} className={styles.exampleTitle}>
                    {t(`providerAccess.guide.styles.${style}.title`)}
                  </h3>
                  <p className={styles.exampleText}>{t(`providerAccess.guide.styles.${style}.text`)}</p>
                </div>
                <IconButton
                  label={
                    copied?.style === style
                      ? t(copied.ok ? "providerAccess.guide.copiedExample" : "providerAccess.copyFailed")
                      : t("providerAccess.guide.copyExample", { style: t(`providerAccess.guide.styles.${style}.title`) })
                  }
                  icon={copied?.style === style && copied.ok ? <CheckIcon /> : <CopyIcon />}
                  onClick={() => void copy(style, code)}
                  align="end"
                />
              </div>
              <pre className={styles.code}>
                <code>{code}</code>
              </pre>
            </section>
          );
        })}

        <div className={styles.note}>
          <p className={styles.noteTitle}>{t("providerAccess.guide.otherTitle")}</p>
          <p>{t("providerAccess.guide.other", { mantle: `https://bedrock-mantle.${region ?? "<region>"}.api.aws/v1` })}</p>
        </div>

        <ul className={styles.links}>
          <li>
            <a href={CompatibilityUrl} target="_blank" rel="noreferrer">
              {t("providerAccess.guide.compatibilityLink")}
              <ExternalIcon className={styles.linkIcon} />
              <span className="visually-hidden">{t("providerAccess.guide.newTab")}</span>
            </a>
          </li>
          <li>
            <a href={AvailabilityUrl} target="_blank" rel="noreferrer">
              {t("providerAccess.guide.availabilityLink")}
              <ExternalIcon className={styles.linkIcon} />
              <span className="visually-hidden">{t("providerAccess.guide.newTab")}</span>
            </a>
          </li>
        </ul>
      </div>
    </Modal>
  );
}
